import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  FiCreditCard, FiFileText, FiClock, FiCheck, FiX, FiDownload,
  FiMail, FiEye, FiCalendar, FiTrendingUp, FiTrendingDown,
  FiAlertCircle, FiCheckCircle, FiClock as FiClockIcon
} from 'react-icons/fi';
import { format, subDays, startOfMonth, endOfMonth, addDays } from 'date-fns';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';
import { toast } from 'react-toastify';
import { formatRandSimple, formatRandNoDecimals } from '../../utils/currency';
import styles from './ClientPortal.module.css';

interface Invoice {
  id: string;
  number: string;
  amount: number;
  status: 'paid' | 'pending' | 'overdue' | 'draft';
  dueDate: Date;
  issueDate: Date;
  client: string;
}

interface Quote {
  id: string;
  number: string;
  amount: number;
  status: 'accepted' | 'pending' | 'rejected' | 'expired';
  validUntil: Date;
  issueDate: Date;
  client: string;
}

// Mock data
const mockInvoices: Invoice[] = [
  {
    id: '1',
    number: 'INV-2024-001',
    amount: 2500,
    status: 'paid',
    dueDate: subDays(new Date(), 5),
    issueDate: subDays(new Date(), 20),
    client: 'Acme Corporation'
  },
  {
    id: '2',
    number: 'INV-2024-002',
    amount: 1800,
    status: 'pending',
    dueDate: addDays(new Date(), 10),
    issueDate: subDays(new Date(), 15),
    client: 'Acme Corporation'
  },
  {
    id: '3',
    number: 'INV-2024-003',
    amount: 3200,
    status: 'overdue',
    dueDate: subDays(new Date(), 3),
    issueDate: subDays(new Date(), 25),
    client: 'Acme Corporation'
  }
];

const mockQuotes: Quote[] = [
  {
    id: '1',
    number: 'Q-2024-001',
    amount: 5000,
    status: 'accepted',
    validUntil: addDays(new Date(), 15),
    issueDate: subDays(new Date(), 10),
    client: 'Acme Corporation'
  },
  {
    id: '2',
    number: 'Q-2024-002',
    amount: 3500,
    status: 'pending',
    validUntil: addDays(new Date(), 20),
    issueDate: subDays(new Date(), 5),
    client: 'Acme Corporation'
  }
];

// Chart data
const monthlyData = [
  { month: 'Jan', invoices: 12000, quotes: 8000 },
  { month: 'Feb', invoices: 15000, quotes: 12000 },
  { month: 'Mar', invoices: 18000, quotes: 15000 },
  { month: 'Apr', invoices: 14000, quotes: 10000 },
  { month: 'May', invoices: 22000, quotes: 18000 },
  { month: 'Jun', invoices: 25000, quotes: 20000 }
];

const statusData = [
  { name: 'Paid', value: 65, color: '#10b981' },
  { name: 'Pending', value: 25, color: '#f59e0b' },
  { name: 'Overdue', value: 10, color: '#ef4444' }
];



export function ClientPortal() {
  const [invoices, setInvoices] = useState<Invoice[]>(mockInvoices);
  const [quotes, setQuotes] = useState<Quote[]>(mockQuotes);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    // Simulate loading
    const timer = setTimeout(() => {
      setIsLoading(false);
    }, 1000);

    return () => clearTimeout(timer);
  }, []);

  const totalInvoiced = invoices.reduce((sum, invoice) => sum + invoice.amount, 0);
  const totalPaid = invoices.filter(inv => inv.status === 'paid').reduce((sum, invoice) => sum + invoice.amount, 0);
  const totalPending = invoices.filter(inv => inv.status === 'pending').reduce((sum, invoice) => sum + invoice.amount, 0);
  const totalOverdue = invoices.filter(inv => inv.status === 'overdue').reduce((sum, invoice) => sum + invoice.amount, 0);

  const getStatusBadge = (status: string) => {
    const statusConfig = {
      paid: { color: 'success', icon: FiCheckCircle, text: 'Paid' },
      pending: { color: 'warning', icon: FiClockIcon, text: 'Pending' },
      overdue: { color: 'danger', icon: FiAlertCircle, text: 'Overdue' },
      draft: { color: 'secondary', icon: FiFileText, text: 'Draft' },
      accepted: { color: 'success', icon: FiCheckCircle, text: 'Accepted' },
      rejected: { color: 'danger', icon: FiX, text: 'Rejected' },
      expired: { color: 'secondary', icon: FiClock, text: 'Expired' }
    };

    const config = statusConfig[status as keyof typeof statusConfig];
    const Icon = config.icon;

    return (
      <span className={`${styles.statusBadge} ${styles[config.color]}`}>
        <Icon size={12} />
        {config.text}
      </span>
    );
  };

  const handlePayInvoice = (invoiceId: string) => {
    // Simulate payment processing
    toast.success('Payment processed successfully!');
  };

  const handleAcceptQuote = (quoteId: string) => {
    // Simulate quote acceptance
    toast.success('Quote accepted successfully!');
  };

  if (isLoading) {
    return (
      <div className={styles.loadingContainer}>
        <div className={styles.loadingSpinner}></div>
        <p>Loading your portal...</p>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      {/* Header */}
      <div className={styles.header}>
        <div className={styles.headerContent}>
          <h1 className={styles.title}>Welcome back, Acme Corporation</h1>
          <p className={styles.subtitle}>Here's an overview of your account</p>
        </div>
        <div className={styles.headerActions}>
          <button className={styles.actionButton}>
            <FiDownload size={16} />
            Export Data
          </button>
          <button className={styles.actionButton}>
            <FiMail size={16} />
            Contact Support
          </button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className={styles.statsGrid}>
        <motion.div 
          className={styles.statCard}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
        >
          <div className={styles.statIcon}>
            <FiCreditCard size={24} />
          </div>
          <div className={styles.statContent}>
            <h3 className={styles.statValue}>{formatRandNoDecimals(totalInvoiced)}</h3>
            <p className={styles.statLabel}>Total Invoiced</p>
          </div>
          <div className={styles.statTrend}>
            <FiTrendingUp size={16} />
            <span>+12.5%</span>
          </div>
        </motion.div>

        <motion.div 
          className={styles.statCard}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
        >
          <div className={styles.statIcon}>
            <FiCheckCircle size={24} />
          </div>
          <div className={styles.statContent}>
            <h3 className={styles.statValue}>{formatRandNoDecimals(totalPaid)}</h3>
            <p className={styles.statLabel}>Total Paid</p>
          </div>
          <div className={styles.statTrend}>
            <FiTrendingUp size={16} />
            <span>+8.3%</span>
          </div>
        </motion.div>

        <motion.div 
          className={styles.statCard}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
        >
          <div className={styles.statIcon}>
            <FiClock size={24} />
          </div>
          <div className={styles.statContent}>
            <h3 className={styles.statValue}>{formatRandNoDecimals(totalPending)}</h3>
            <p className={styles.statLabel}>Pending Payment</p>
          </div>
          <div className={styles.statTrend}>
            <FiTrendingDown size={16} />
            <span>-5.2%</span>
          </div>
        </motion.div>

        <motion.div 
          className={styles.statCard}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
        >
          <div className={styles.statIcon}>
            <FiAlertCircle size={24} />
          </div>
          <div className={styles.statContent}>
            <h3 className={styles.statValue}>{formatRandNoDecimals(totalOverdue)}</h3>
            <p className={styles.statLabel}>Overdue Amount</p>
          </div>
          <div className={styles.statTrend}>
            <FiTrendingDown size={16} />
            <span>-15.7%</span>
          </div>
        </motion.div>
      </div>

      <div className={styles.content}>
        {/* Charts Section */}
        <div className={styles.chartsSection}>
          <motion.div 
            className={styles.chartCard}
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.5 }}
          >
            <h3 className={styles.chartTitle}>Monthly Overview</h3>
            <ResponsiveContainer width="100%" height={300}>
              <LineChart data={monthlyData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="month" />
                <YAxis />
                <Tooltip />
                <Line type="monotone" dataKey="invoices" stroke="#667eea" strokeWidth={2} />
                <Line type="monotone" dataKey="quotes" stroke="#764ba2" strokeWidth={2} />
              </LineChart>
            </ResponsiveContainer>
          </motion.div>

          <motion.div 
            className={styles.chartCard}
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.6 }}
          >
            <h3 className={styles.chartTitle}>Invoice Status</h3>
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie
                  data={statusData}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                  outerRadius={80}
                  fill="#8884d8"
                  dataKey="value"
                >
                  {statusData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          </motion.div>
        </div>

        {/* Recent Activity */}
        <div className={styles.activitySection}>
          <motion.div 
            className={styles.activityCard}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.7 }}
          >
            <div className={styles.cardHeader}>
              <h3 className={styles.cardTitle}>Recent Invoices</h3>
              <Link to="/portal/invoices" className={styles.viewAllLink}>
                View All
              </Link>
            </div>
            
            <div className={styles.invoiceList}>
              {invoices.slice(0, 5).map((invoice) => (
                <div key={invoice.id} className={styles.invoiceItem}>
                  <div className={styles.invoiceInfo}>
                    <h4 className={styles.invoiceNumber}>{invoice.number}</h4>
                    <p className={styles.invoiceDate}>
                      Due: {format(invoice.dueDate, 'MMM dd, yyyy')}
                    </p>
                  </div>
                  <div className={styles.invoiceAmount}>
                    <span className={styles.amount}>{formatRandNoDecimals(invoice.amount)}</span>
                    {getStatusBadge(invoice.status)}
                  </div>
                  <div className={styles.invoiceActions}>
                    <button className={styles.actionBtn}>
                      <FiEye size={16} />
                    </button>
                    <button className={styles.actionBtn}>
                      <FiDownload size={16} />
                    </button>
                    {invoice.status === 'pending' && (
                      <button 
                        className={`${styles.actionBtn} ${styles.payBtn}`}
                        onClick={() => handlePayInvoice(invoice.id)}
                      >
                        <FiCreditCard size={16} />
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </motion.div>

          <motion.div 
            className={styles.activityCard}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.8 }}
          >
            <div className={styles.cardHeader}>
              <h3 className={styles.cardTitle}>Recent Quotes</h3>
              <Link to="/portal/quotes" className={styles.viewAllLink}>
                View All
              </Link>
            </div>
            
            <div className={styles.quoteList}>
              {quotes.slice(0, 5).map((quote) => (
                <div key={quote.id} className={styles.quoteItem}>
                  <div className={styles.quoteInfo}>
                    <h4 className={styles.quoteNumber}>{quote.number}</h4>
                    <p className={styles.quoteDate}>
                      Valid until: {format(quote.validUntil, 'MMM dd, yyyy')}
                    </p>
                  </div>
                  <div className={styles.quoteAmount}>
                    <span className={styles.amount}>{formatRandNoDecimals(quote.amount)}</span>
                    {getStatusBadge(quote.status)}
                  </div>
                  <div className={styles.quoteActions}>
                    <button className={styles.actionBtn}>
                      <FiEye size={16} />
                    </button>
                    <button className={styles.actionBtn}>
                      <FiDownload size={16} />
                    </button>
                    {quote.status === 'pending' && (
                      <button 
                        className={`${styles.actionBtn} ${styles.acceptBtn}`}
                        onClick={() => handleAcceptQuote(quote.id)}
                      >
                        <FiCheck size={16} />
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </motion.div>
        </div>

        {/* Quick Actions */}
        <motion.div 
          className={styles.quickActions}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.9 }}
        >
          <h3 className={styles.quickActionsTitle}>Quick Actions</h3>
          <div className={styles.actionsGrid}>
            <Link to="/portal/invoices" className={styles.actionCard}>
              <FiFileText size={24} />
              <span>View All Invoices</span>
            </Link>
            <Link to="/portal/quotes" className={styles.actionCard}>
              <FiFileText size={24} />
              <span>View All Quotes</span>
            </Link>
            <button className={styles.actionCard}>
              <FiMail size={24} />
              <span>Contact Support</span>
            </button>
            <button className={styles.actionCard}>
              <FiDownload size={24} />
              <span>Download Reports</span>
            </button>
          </div>
        </motion.div>
      </div>
    </div>
  );
}
